// The public booking page's client (spec §5, §7.1, §11): hand-written,
// because the routes are out of the OpenAPI schema (routes/booking_public.py,
// include_in_schema=False), and with no react-query, toaster or session:
// a booker has no Lattice login. JSON only (a cross-site form cannot post
// without a preflight nobody answers); files go up raw with an X-Hold
// header through XMLHttpRequest, the one way a browser reports upload
// progress. No cookie is sent.
//
// Every refusal is {detail: {code, message, slots?, pow?, field?}}; a
// request that never reached the server is a BookError with status 0.

export type Place = "meet" | "phone" | "in_person";

export interface PowChallenge {
  challenge: string;
  bits: number;
}

export interface PublicQuestion {
  id: string;
  label: string;
  kind: "text" | "long" | "url" | "phone" | "choice" | "file" | "file_or_link";
  required?: boolean;
  options?: string[];
}

export interface PublicPage {
  title: string;
  host_name: string;
  intro?: string;
  length_min?: number;
  place?: Place;
  location?: string;
  with?: string[];
  questions?: PublicQuestion[];
  tz?: string;
  takes_files?: boolean;
  /** Optional (needs_from_others): the rolling horizon and the link's end date, for the no-times words. */
  horizon_days?: number | null;
  ends_on?: string | null;
}

export interface PublicPageOut {
  state: "open" | "unavailable" | "full";
  page: PublicPage;
  slots?: string[];
  until?: string | null;
  pow?: PowChallenge | null;
}

export interface HoldBody {
  start?: string;
  tz?: string;
  pow?: { challenge: string; nonce: string };
  previous?: string;
  renew?: string;
}

export interface HoldOut {
  hold: string;
  start: string;
  end: string;
  hides: boolean;
  pow?: PowChallenge | null;
}

export interface StagedFile {
  id: string;
  name: string;
  size: number;
}

export interface BookingBody {
  hold: string;
  name: string;
  email: string;
  answers: Record<string, string>;
  files: string[];
  tz: string;
  website: string;
}

export interface BookingOut {
  state: "booked" | "unknown";
  start?: string | null;
  end?: string | null;
  title?: string;
  host_name?: string;
  with?: string[];
  place?: Place | null;
  location?: string;
  meet_link?: string | null;
  invite_to?: string;
  manage?: string;
}

export interface ManageBody {
  token: string;
  do: "view" | "slots" | "move" | "cancel";
  email?: string;
  start?: string;
  note?: string;
}

export interface ManageOut {
  state: "booked" | "moved" | "cancelled";
  title: string;
  host_name: string;
  start: string;
  end: string;
  tz?: string;
  length_min?: number;
  place?: Place | null;
  location?: string;
  meet_link?: string | null;
  can_move?: boolean;
  can_cancel?: boolean;
  asked?: boolean;
  page_deleted?: boolean;
  slots?: string[];
  /** Optional (needs_from_others): the page's horizon and the link's end date, for "No other times free in the next 3 weeks." */
  horizon_days?: number | null;
  ends_on?: string | null;
}

export class BookError extends Error {
  status: number;
  code: string;
  slots: string[] | null;
  pow: PowChallenge | null;
  field: string | null;
  retryAfter: number | null;

  constructor(status: number, code: string, message: string, extra: Record<string, unknown> = {}, retryAfter: number | null = null) {
    super(message);
    this.name = "BookError";
    this.status = status;
    this.code = code;
    this.slots = Array.isArray(extra.slots) ? (extra.slots as string[]) : null;
    const pow = extra.pow as PowChallenge | undefined;
    this.pow = pow && typeof pow.challenge === "string" ? pow : null;
    this.field = typeof extra.field === "string" ? extra.field : null;
    this.retryAfter = retryAfter;
  }
}

/** A request that never reached the server, or a 5xx with no refusal of ours behind it. */
export const isLost = (e: unknown) => e instanceof BookError && (e.status === 0 || (e.status >= 500 && e.code === "server"));

function refusal(status: number, text: string, retryAfter: string | null): BookError {
  let detail: Record<string, unknown> = {};
  try {
    const body = JSON.parse(text) as { detail?: unknown };
    if (body && typeof body.detail === "object" && body.detail) detail = body.detail as Record<string, unknown>;
  } catch {
    // an edge or proxy page: no refusal of ours
  }
  const code = typeof detail.code === "string" ? detail.code : "server";
  const message = typeof detail.message === "string" ? detail.message : "";
  const after = retryAfter && /^\s*\d+\s*$/.test(retryAfter) ? Number(retryAfter) : null;
  return new BookError(status, code, message, detail, after);
}

async function call<T>(path: string, body?: unknown): Promise<{ status: number; data: T }> {
  let resp: Response;
  try {
    resp = await fetch(path, {
      method: body === undefined ? "GET" : "POST",
      headers: body === undefined ? { Accept: "application/json" } : { "Content-Type": "application/json", Accept: "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "omit",
      cache: "no-store",
      referrerPolicy: "no-referrer",
    });
  } catch {
    throw new BookError(0, "network", "");
  }
  const text = await resp.text().catch(() => "");
  if (!resp.ok) throw refusal(resp.status, text, resp.headers.get("Retry-After"));
  try {
    return { status: resp.status, data: JSON.parse(text) as T };
  } catch {
    throw new BookError(resp.status >= 500 ? resp.status : 502, "server", "");
  }
}

const slugPath = (slug: string) => `/api/book/${encodeURIComponent(slug)}`;

export async function openPage(slug: string): Promise<PublicPageOut> {
  return (await call<PublicPageOut>(slugPath(slug))).data;
}

export async function holdTime(slug: string, body: HoldBody): Promise<HoldOut> {
  return (await call<HoldOut>(`${slugPath(slug)}/hold`, body)).data;
}

/** 201 booked, 202 unknown (the answer was lost: re-send the same hold). */
export async function sendBooking(slug: string, body: BookingBody): Promise<BookingOut> {
  return (await call<BookingOut>(`${slugPath(slug)}/booking`, body)).data;
}

export async function manageBooking(body: ManageBody): Promise<ManageOut> {
  return (await call<ManageOut>("/api/book/manage", body)).data;
}

export interface Upload {
  promise: Promise<StagedFile>;
  abort: () => void;
}

/** Streams one file to the hold; `onProgress` gets 0-100 while it goes up. */
export function uploadFile(slug: string, hold: string, file: Blob & { name: string }, onProgress: (pct: number) => void): Upload {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<StagedFile>((resolve, reject) => {
    xhr.open("POST", `${slugPath(slug)}/files`);
    xhr.setRequestHeader("X-Hold", hold);
    xhr.setRequestHeader("X-File-Name", encodeURIComponent(file.name));
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    xhr.withCredentials = false;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.min(99, Math.floor((e.loaded / e.total) * 100)));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve((JSON.parse(xhr.responseText) as { file: StagedFile }).file);
        } catch {
          reject(new BookError(502, "server", ""));
        }
      } else {
        reject(refusal(xhr.status, xhr.responseText, xhr.getResponseHeader("Retry-After")));
      }
    };
    xhr.onerror = () => reject(new BookError(0, "network", ""));
    xhr.onabort = () => reject(new BookError(0, "aborted", ""));
    xhr.send(file);
  });
  return { promise, abort: () => xhr.abort() };
}

// ─── the parallel start (C15) ───
//
// App.tsx calls startBookingLoad(pathname) before the page's chunk has
// loaded, so the GET and the chunk travel together; the page then takes
// the answer already on its way instead of asking again.

/** /book and anything below it (the public page); /books and /bookings stay app paths. */
export function isBookingPath(pathname: string): boolean {
  return pathname === "/book" || pathname.startsWith("/book/");
}

/** The slug of /book/<slug>, or null for /book, /book/manage and anything else. */
export function slugFrom(pathname: string): string | null {
  const m = /^\/book\/([^/]+)\/?$/.exec(pathname);
  if (!m) return null;
  let slug: string;
  try {
    slug = decodeURIComponent(m[1]);
  } catch {
    return null;
  }
  return slug === "manage" ? null : slug;
}

let pending: { slug: string; promise: Promise<PublicPageOut> } | null = null;
let started = false;

/** Once per page load, whatever re-renders App (each GET counts an open). */
export function startBookingLoad(pathname: string): void {
  const slug = slugFrom(pathname);
  if (!slug || started) return;
  started = true;
  const promise = openPage(slug);
  promise.catch(() => undefined); // read by the page; never an unhandled rejection meanwhile
  pending = { slug, promise };
}

/** The GET started early for this slug (once), else a fresh one. */
export function takeBookingLoad(slug: string): Promise<PublicPageOut> {
  if (pending && pending.slug === slug) {
    const { promise } = pending;
    pending = null;
    return promise;
  }
  return openPage(slug);
}
